/* eslint-disable react/jsx-props-no-spreading */
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import CustomModalHeader from 'components/modal/customModalHeader';
import GroupButton from 'components/button/groupButton';
import { toast } from 'react-toastify';
import { messageErrors } from 'utils/utils';
import { Modal, Label } from 'flowbite-react';
import { useEffect, useRef, useState } from 'react';
import { themModal } from 'utils/theme';
import { notifyVendorShortage } from 'api/purchaseOrderApi';

type NotifyVendorShortageModalProps = {
  openModal: boolean;
  setOpenModal: (val: boolean) => void;
  purchaseOrder: any;
  purchaseOrderItems: any[];
  vendorName: string;
  vendorContactEmail: string;
};

const NotifyVendorShortageModal = (props: NotifyVendorShortageModalProps) => {
  const { openModal, setOpenModal, purchaseOrder, purchaseOrderItems, vendorName, vendorContactEmail } = props;
  const [t] = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);

  const vendorOrderId = purchaseOrder?.vendorOrderId || purchaseOrder?.id || '';

  const getMissingItems = () => {
    return purchaseOrderItems
      .filter((item: any) => {
        const ordered = item.orderCount || item.quantityOrdered || 0;
        const received = item.quantityReceived || 0;
        return ordered > received;
      })
      .map((item: any) => {
        const ordered = item.orderCount || item.quantityOrdered || 0;
        const received = item.quantityReceived || 0;
        const missing = ordered - received;
        const itemName = item.item?.name || item.itemName || 'Unknown Item';
        return `${itemName} - Quantity ${missing} (Case Order Quantity not received)`;
      });
  };

  const buildDefaultBody = () => {
    const missingItems = getMissingItems();
    const itemsList = missingItems.map((item: string, idx: number) => `${idx + 1}. ${item}`).join('\n');
    return `Hello ${vendorName},\n\nWe are writing to inform you that the following items from Purchase Order #${vendorOrderId} were not received in the shipment:\n\n${itemsList}\n\nPlease advise on the status of these missing items at your earliest convenience.`;
  };

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [toEmail, setToEmail] = useState('');

  useEffect(() => {
    if (openModal) {
      setSubject(`Purchase order #${vendorOrderId} item shortage`);
      setBody(buildDefaultBody());
      setToEmail(vendorContactEmail);
    }
    // eslint-disable-next-line
  }, [openModal]);

  const handleSend = async () => {
    if (!toEmail) {
      const msg: string = t('orderPage.vendorShortage.emailRequired');
      toast.error(msg);
      throw new Error(msg);
    }
    await notifyVendorShortage(purchaseOrder.id, { subject, body, toEmail });
  };

  const mutation = useMutation(['notify-vendor-shortage'], { mutationFn: handleSend });

  const onSubmit = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        const msg: string = t('orderPage.vendorShortage.sendSuccess');
        toast.success(msg);
        toggleHandler();
      },
      onError: (error: any) => {
        if (error?.message === t('orderPage.vendorShortage.emailRequired')) return;
        const message: string = messageErrors(error, t);
        toast.error(message);
      },
    });
  };

  const toggleHandler = () => {
    setOpenModal(false);
  };

  return (
    <div ref={rootRef}>
      <Modal
        show={openModal}
        size="4xl"
        root={rootRef.current ?? undefined}
        onClose={toggleHandler}
        dismissible={true}
        theme={themModal}
      >
        <CustomModalHeader title={t('orderPage.vendorShortage.title')} toggle={toggleHandler} />
        <Modal.Body theme={{ base: 'flex-12 pb-4 pt-3 px-12' }}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="toEmail" className="mb-1 block font-medium text-sm">
                {t('orderPage.vendorShortage.toEmail')}
              </Label>
              <input
                id="toEmail"
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="vendor@example.com"
              />
            </div>
            <div>
              <Label htmlFor="subject" className="mb-1 block font-medium text-sm">
                {t('orderPage.vendorShortage.subject')}
              </Label>
              <input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <Label htmlFor="body" className="mb-1 block font-medium text-sm">
                {t('orderPage.vendorShortage.body')}
              </Label>
              <textarea
                id="body"
                rows={12}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
          <GroupButton
            className="items-center justify-center pt-5 mb-4"
            buttons={[
              {
                type: 'button',
                text: t('modal.cancel'),
                classType: 'white',
                action: () => toggleHandler(),
              },
              {
                type: 'button',
                text: t('orderPage.vendorShortage.send'),
                classType: 'blue',
                isLoading: mutation.isLoading,
                action: () => onSubmit(),
              },
            ]}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NotifyVendorShortageModal;
